import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { fetchBatchById } from '../pages/services/batchService';
import { fetchStudentById, sendStudentNotification, fetchWhatsAppTemplates } from '../pages/services/studentService';
import { fetchAssignmentsByBatchId } from '../pages/services/assignmentService';
import { fetchLogsByStudentId } from '../pages/services/logService';
import AddPerformanceModal from './AddPerfomanceModal';
import NotificationHistorySection from './NotificationHistorySection';
import "../styles/StudentDetails.css";

const BUDDY_BASE = "https://buddy.myskillverse.ai/assignment.html";

const PARAM_OPTIONS = [
    { value: 'student_name', label: 'Student Name' },
    { value: 'parent_name', label: 'Parent Name' },
    { value: 'batch_name', label: 'Batch Name' },
    { value: 'assignment_link', label: 'Assignment Link' },
    { value: 'custom', label: 'Custom' },
];

const buildPreview = (body, paramNames, paramSelections, student, batch) => {
    if (!body) return '';
    let msg = body;
    paramNames.forEach((_, idx) => {
        const sel = paramSelections[idx];
        let val = '';
        if (!sel || !sel.type) {
            val = `{{${idx + 1}}}`;
        } else if (sel.type === 'student_name') {
            val = student?.studentName || '[Student Name]';
        } else if (sel.type === 'parent_name') {
            val = student?.fatherName || student?.motherName || '[Parent Name]';
        } else if (sel.type === 'batch_name') {
            val = batch?.batchTitle || '[Batch Name]';
        } else if (sel.type === 'assignment_link') {
            val = sel.value || '[Assignment Link]';
        } else if (sel.type === 'custom') {
            val = sel.value || '[Custom]';
        }
        msg = msg.replace(`{{${idx + 1}}}`, val);
    });
    return msg;
};

const buildEmailBodyPreview = (body, paramSelections, student, batch) => {
    if (!body) return '';
    let text = body;
    Object.entries(paramSelections).forEach(([num, sel]) => {
        let val = `{${num}}`;
        if (sel.type === 'student_name') val = student?.studentName || '[Student Name]';
        else if (sel.type === 'parent_name') val = student?.fatherName || student?.motherName || '[Parent Name]';
        else if (sel.type === 'batch_name') val = batch?.batchTitle || '[Batch Name]';
        else if (sel.type === 'assignment_link') val = sel.value || '[Assignment Link]';
        else if (sel.type === 'custom') val = sel.value || '[Custom]';
        text = text.replaceAll(`{${num}}`, val);
    });
    return text;
};

const resolveStudentEmailBody = (body, paramSelections, student, batch) => {
    if (!body) return '';
    let text = body;
    Object.entries(paramSelections).forEach(([num, sel]) => {
        let val = '';
        if (sel.type === 'student_name') val = student?.studentName || '';
        else if (sel.type === 'parent_name') val = student?.fatherName || student?.motherName || '';
        else if (sel.type === 'batch_name') val = batch?.batchTitle || '';
        else if (sel.type === 'assignment_link') val = sel.value || '';
        else if (sel.type === 'custom') val = sel.value || '';
        text = text.split(`{${num}}`).join(val);
    });
    return text;
};

const SendNotificationModal = ({ isOpen, onClose, student, batch, assignments }) => {
    const [notifType, setNotifType] = useState('email');
    const [emailTitle, setEmailTitle] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [emailParamSelections, setEmailParamSelections] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [sendStatus, setSendStatus] = useState(null);
    const [sentTypes, setSentTypes] = useState(new Set());
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [paramSelections, setParamSelections] = useState([]);
    const fileInputRef = useRef(null);

    const emailParamNums = emailBody
        ? [...new Set([...emailBody.matchAll(/\{(\d+)\}/g)].map(m => m[1]))]
            .sort((a, b) => Number(a) - Number(b))
        : [];

    useEffect(() => {
        if (notifType === 'whatsapp' && templates.length === 0) {
            setTemplatesLoading(true);
            fetchWhatsAppTemplates()
                .then(setTemplates)
                .catch(() => setTemplates([]))
                .finally(() => setTemplatesLoading(false));
        }
    }, [notifType]);

    const paramNames = selectedTemplate?.params
        ? selectedTemplate.params.split(',').map(p => p.trim()).filter(Boolean)
        : [];

    const previewText = buildPreview(
        selectedTemplate?.body || '',
        paramNames,
        paramSelections,
        student,
        batch
    );

    const resetForm = () => {
        setNotifType('email');
        setEmailTitle('');
        setEmailBody('');
        setEmailParamSelections({});
        setAttachments([]);
        setShowPreview(false);
        setSendStatus(null);
        setSentTypes(new Set());
        setSelectedTemplate(null);
        setParamSelections([]);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleAttachmentChange = (e) => {
        const files = Array.from(e.target.files);
        setAttachments(prev => [...prev, ...files]);
        e.target.value = '';
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleTemplateChange = (templateId) => {
        const tmpl = templates.find(t => t._id === templateId) || null;
        setSelectedTemplate(tmpl);
        if (tmpl) {
            const names = tmpl.params ? tmpl.params.split(',').map(p => p.trim()).filter(Boolean) : [];
            setParamSelections(names.map(() => ({ type: '', value: '' })));
        } else {
            setParamSelections([]);
        }
    };

    const handleParamTypeChange = (idx, type) => {
        setParamSelections(prev => {
            const next = [...prev];
            next[idx] = { type, value: '' };
            return next;
        });
    };

    const handleParamValueChange = (idx, value) => {
        setParamSelections(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], value };
            return next;
        });
    };

    const handleEmailParamTypeChange = (num, type) => {
        setEmailParamSelections(prev => ({ ...prev, [num]: { type, value: '', assignmentId: '' } }));
    };

    const handleEmailParamValueChange = (num, value) => {
        setEmailParamSelections(prev => ({ ...prev, [num]: { ...prev[num], value } }));
    };

    const handleEmailParamAssignmentChange = (num, assignmentId) => {
        const asgn = (assignments || []).find(a => a._id === assignmentId);
        setEmailParamSelections(prev => ({
            ...prev,
            [num]: { type: 'assignment_link', assignmentId, value: asgn ? `${BUDDY_BASE}?id=${asgn._id}` : '' },
        }));
    };

    const canSendWhatsApp = selectedTemplate &&
        paramNames.every((_, idx) => {
            const sel = paramSelections[idx];
            if (!sel?.type) return false;
            if (sel.type === 'custom' && !sel.value?.trim()) return false;
            if (sel.type === 'assignment_link' && !sel.value?.trim()) return false;
            return true;
        });

    const handleSend = async () => {
        setIsSending(true);
        setSendStatus(null);
        try {
            const resolvedBody = emailParamNums.length > 0
                ? resolveStudentEmailBody(emailBody, emailParamSelections, student, batch)
                : emailBody;
            await sendStudentNotification(
                student._id,
                notifType,
                emailTitle,
                resolvedBody,
                attachments,
                notifType === 'whatsapp'
                    ? { templateName: selectedTemplate.templateName, paramSelections }
                    : null
            );
            setSendStatus('success');
            setSentTypes(prev => new Set([...prev, notifType]));
        } catch {
            setSendStatus('error');
        } finally {
            setIsSending(false);
        }
    };

    const getFileIcon = (file) => {
        const type = file.type;
        if (type.startsWith('image/')) return '🖼️';
        if (type.startsWith('video/')) return '🎬';
        if (type === 'application/pdf') return '📄';
        return '📎';
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (!isOpen) return null;

    return (
        <div className="notif-overlay" onClick={handleClose}>
            <div className="notif-modal" onClick={e => e.stopPropagation()}>
                <div className="notif-modal-header">
                    <h2>Send a Notification</h2>
                    <button className="notif-close-btn" onClick={handleClose}>×</button>
                </div>

                <div className="notif-modal-body">
                    <div className="notif-radio-group">
                        <label className={`notif-radio-option ${notifType === 'email' ? 'notif-radio-selected' : ''}`}>
                            <input type="radio" name="notifType" value="email" checked={notifType === 'email'}
                                onChange={() => { setNotifType('email'); setShowPreview(false); }} />
                            <span className="notif-radio-icon">✉️</span> Email
                        </label>
                        <label className={`notif-radio-option ${notifType === 'whatsapp' ? 'notif-radio-selected' : ''}`}>
                            <input type="radio" name="notifType" value="whatsapp" checked={notifType === 'whatsapp'}
                                onChange={() => { setNotifType('whatsapp'); setShowPreview(false); }} />
                            <span className="notif-radio-icon">💬</span> WhatsApp
                        </label>
                    </div>

                    {/* Email Fields */}
                    {notifType === 'email' && (
                        <div className="notif-email-fields">
                            <div className="notif-field">
                                <label className="notif-label">Subject</label>
                                <input type="text" value={emailTitle} onChange={e => setEmailTitle(e.target.value)}
                                    placeholder="Enter email subject..." className="notif-input" />
                            </div>
                            <div className="notif-field">
                                <label className="notif-label">
                                    Body
                                    <span className="notif-param-hint">Use {"{1}"}, {"{2}"}… as placeholders — dropdowns appear below</span>
                                </label>
                                <textarea value={emailBody} onChange={e => setEmailBody(e.target.value)}
                                    placeholder={"Write your email content here…\nTip: use {1}, {2} for dynamic values like student name, assignment link, etc."}
                                    className="notif-textarea" rows={5} />
                            </div>

                            {emailParamNums.length > 0 && (
                                <div className="notif-field">
                                    <label className="notif-label">Placeholder Values</label>
                                    <div className="wa-params-list">
                                        {emailParamNums.map((num) => (
                                            <div key={num} className="wa-param-row">
                                                <span className="wa-param-name">{`{${num}}`}</span>
                                                <div className="wa-param-select-wrap">
                                                    <select
                                                        className="notif-input wa-select"
                                                        value={emailParamSelections[num]?.type || ''}
                                                        onChange={(e) => handleEmailParamTypeChange(num, e.target.value)}
                                                    >
                                                        <option value="">Select value…</option>
                                                        {PARAM_OPTIONS.map((opt) => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                    {emailParamSelections[num]?.type === 'custom' && (
                                                        <textarea
                                                            className="notif-textarea wa-custom-textarea"
                                                            rows={2}
                                                            placeholder="Enter custom value or URL…"
                                                            value={emailParamSelections[num]?.value || ''}
                                                            onChange={(e) => handleEmailParamValueChange(num, e.target.value)}
                                                        />
                                                    )}
                                                    {emailParamSelections[num]?.type === 'assignment_link' && (
                                                        <div className="wa-assignment-picker">
                                                            <select
                                                                className="notif-input wa-select"
                                                                value={emailParamSelections[num]?.assignmentId || ''}
                                                                onChange={(e) => handleEmailParamAssignmentChange(num, e.target.value)}
                                                            >
                                                                <option value="">Select assignment…</option>
                                                                {(assignments || []).map((a) => {
                                                                    const parts = (a.assignmentTime || '').split(' - ');
                                                                    const fmt = (s) => { const d = new Date(s?.trim()); return isNaN(d) ? s : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
                                                                    const window = parts.length === 2 ? ` | ${fmt(parts[0])} → ${fmt(parts[1])}` : '';
                                                                    return <option key={a._id} value={a._id}>{a.assignmentTitle}{window}</option>;
                                                                })}
                                                            </select>
                                                            {emailParamSelections[num]?.value && (
                                                                <span className="wa-assignment-link-preview">{emailParamSelections[num].value}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="notif-field">
                                <label className="notif-label">Attachments</label>
                                <input ref={fileInputRef} type="file" multiple onChange={handleAttachmentChange}
                                    className="notif-file-input-hidden" id="notif-file-input" />
                                <button type="button" className="notif-file-btn" onClick={() => fileInputRef.current?.click()}>
                                    + Choose Files
                                </button>
                                <span className="notif-file-hint">PDF, Images, Videos and more supported</span>
                                {attachments.length > 0 && (
                                    <div className="notif-attachments-list">
                                        {attachments.map((file, idx) => (
                                            <div key={idx} className="notif-attachment-chip">
                                                <span className="notif-attachment-icon">{getFileIcon(file)}</span>
                                                <span className="notif-attachment-name">{file.name}</span>
                                                <span className="notif-attachment-size">({formatFileSize(file.size)})</span>
                                                <button className="notif-attachment-remove" onClick={() => removeAttachment(idx)}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button type="button" className="notif-preview-toggle" onClick={() => setShowPreview(p => !p)}>
                                {showPreview ? '▲ Hide Preview' : '▼ Show Preview'}
                            </button>
                            {showPreview && (
                                <div className="notif-preview-wrapper">
                                    <div className="notif-preview-bar">
                                        <span>Email Preview</span>
                                        <span className="notif-preview-hint">As seen by the recipient</span>
                                    </div>
                                    <div className="notif-email-preview">
                                        <div className="preview-email-chrome">
                                            <div className="preview-chrome-dots"><span></span><span></span><span></span></div>
                                            <span className="preview-chrome-title">Mail</span>
                                        </div>
                                        <div className="preview-email-body-wrapper">
                                            <div className="preview-meta-row">
                                                <span className="preview-field-label">From</span>
                                                <span className="preview-field-value">SkillVerse &lt;noreply@skillverse.in&gt;</span>
                                            </div>
                                            <div className="preview-meta-row">
                                                <span className="preview-field-label">To</span>
                                                <span className="preview-field-value">
                                                    {student?.studentName} &lt;{
                                                        Array.isArray(student?.emailId)
                                                            ? student.emailId.join(', ')
                                                            : (student?.emailId || 'no-email@record.com')
                                                    }&gt;
                                                </span>
                                            </div>
                                            <div className="preview-meta-row preview-subject-row">
                                                <span className="preview-subject-text">
                                                    {emailTitle || <em className="preview-empty">No subject</em>}
                                                </span>
                                            </div>
                                            <div className="preview-email-content">
                                                {emailBody
                                                    ? <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{buildEmailBodyPreview(emailBody, emailParamSelections, student, batch)}</p>
                                                    : <p className="preview-empty">Email body will appear here...</p>}
                                            </div>
                                            {attachments.length > 0 && (
                                                <div className="preview-attachments-row">
                                                    <span className="preview-attachments-label">📎 Attachments</span>
                                                    <div className="preview-attachment-chips">
                                                        {attachments.map((file, idx) => (
                                                            <span key={idx} className="preview-attachment-pill">
                                                                {getFileIcon(file)} {file.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* WhatsApp Fields */}
                    {notifType === 'whatsapp' && (
                        <div className="notif-email-fields">
                            {/* Template dropdown */}
                            <div className="notif-field">
                                <label className="notif-label">Template</label>
                                {templatesLoading ? (
                                    <span className="notif-file-hint">Loading templates...</span>
                                ) : (
                                    <select
                                        className="notif-input wa-select"
                                        value={selectedTemplate?._id || ''}
                                        onChange={e => handleTemplateChange(e.target.value)}
                                    >
                                        <option value="">Select a template...</option>
                                        {templates.map(t => (
                                            <option key={t._id} value={t._id}>{t.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Params */}
                            {selectedTemplate && paramNames.length > 0 && (
                                <div className="notif-field">
                                    <label className="notif-label">Parameters</label>
                                    <div className="wa-params-list">
                                        {paramNames.map((paramName, idx) => (
                                            <div key={idx} className="wa-param-row">
                                                <span className="wa-param-name">{paramName}</span>
                                                <div className="wa-param-select-wrap">
                                                    <select
                                                        className="notif-input wa-select"
                                                        value={paramSelections[idx]?.type || ''}
                                                        onChange={e => handleParamTypeChange(idx, e.target.value)}
                                                    >
                                                        <option value="">Select value...</option>
                                                        {PARAM_OPTIONS.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                    {paramSelections[idx]?.type === 'custom' && (
                                                        <textarea
                                                            className="notif-textarea wa-custom-textarea"
                                                            rows={2}
                                                            placeholder="Enter custom value or URL..."
                                                            value={paramSelections[idx]?.value || ''}
                                                            onChange={e => handleParamValueChange(idx, e.target.value)}
                                                        />
                                                    )}
                                                    {paramSelections[idx]?.type === 'assignment_link' && (
                                                        <div className="wa-assignment-picker">
                                                            <select
                                                                className="notif-input wa-select"
                                                                value={paramSelections[idx]?.assignmentId || ''}
                                                                onChange={(e) => {
                                                                    const asgn = (assignments || []).find(a => a._id === e.target.value);
                                                                    setParamSelections(prev => {
                                                                        const next = [...prev];
                                                                        next[idx] = { type: 'assignment_link', assignmentId: e.target.value, value: asgn ? `${BUDDY_BASE}?id=${asgn._id}` : '' };
                                                                        return next;
                                                                    });
                                                                }}
                                                            >
                                                                <option value="">Select assignment...</option>
                                                                {(assignments || []).map((a) => {
                                                                    const parts = (a.assignmentTime || '').split(' - ');
                                                                    const fmt = (s) => { const d = new Date(s?.trim()); return isNaN(d) ? s : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); };
                                                                    const window = parts.length === 2 ? ` | ${fmt(parts[0])} → ${fmt(parts[1])}` : '';
                                                                    return <option key={a._id} value={a._id}>{a.assignmentTitle}{window}</option>;
                                                                })}
                                                            </select>
                                                            {paramSelections[idx]?.value && (
                                                                <span className="wa-assignment-link-preview">{paramSelections[idx].value}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* WhatsApp Preview */}
                            {selectedTemplate && (
                                <div className="wa-preview-wrapper">
                                    <div className="wa-preview-bar">
                                        <span>WhatsApp Preview</span>
                                        <span className="notif-preview-hint">Updates as you fill params</span>
                                    </div>
                                    <div className="wa-preview-screen">
                                        <div className="wa-preview-bubble">
                                            <p className="wa-preview-text" style={{ whiteSpace: 'pre-wrap' }}>
                                                {previewText || <em style={{ color: '#9ca3af' }}>Fill in params to see preview...</em>}
                                            </p>
                                            <span className="wa-preview-meta">10:30 AM ✓✓</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <span className="notif-file-hint">
                                Will be sent to {student?.contactNumber || 'student contact number'}
                            </span>
                        </div>
                    )}

                    {sendStatus === 'success' && (
                        <div className="notif-status notif-status-success">Notification sent successfully!</div>
                    )}
                    {sendStatus === 'error' && (
                        <div className="notif-status notif-status-error">Failed to send notification. Please try again.</div>
                    )}
                </div>

                <div className="notif-modal-footer">
                    <button className="notif-cancel-btn" onClick={handleClose} disabled={isSending}>Cancel</button>
                    <button
                        className="notif-send-btn"
                        onClick={handleSend}
                        disabled={isSending || sentTypes.has(notifType) || (notifType === 'email' ? !emailTitle && !emailBody : !canSendWhatsApp)}
                    >
                        {isSending ? 'Sending...' : `Send ${notifType === 'email' ? 'Email' : 'WhatsApp'}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

const StudentDetails = () => {
    const { id } = useParams();
    const [student, setStudent] = useState(null);
    const [batch, setBatch] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const navigate = useNavigate();

    const handlePerformanceAdded = (newItem) => {
        setStudent(prev => ({
            ...prev,
            performance: [...(prev.performance || []), newItem]
        }));
    };

    useEffect(() => {
        const getStudent = async () => {
            const delay = new Promise(resolve => setTimeout(resolve, 2000));
            try {
                const [data] = await Promise.all([fetchStudentById(id), delay]);
                setStudent(data);
            } catch (err) {
                setError('Error fetching student details');
            } finally {
                setLoading(false);
            }
        };

        getStudent();
    }, [id]);


    useEffect(() => {
        const getBatch = async () => {
            try {
                const batchId = student?.batchInfo?._id;
                if (batchId) {
                    const batchData = await fetchBatchById(batchId);
                    setBatch(batchData);
                } else {
                    console.warn("No batchInfo found in student data.");
                }
            } catch (error) {
                console.error("Error while fetching batch:", error);
            } finally {
                console.log("Batch fetch ended.");
            }
        };

        if (student) {
            getBatch();
        }
    }, [student]);

    useEffect(() => {
        const batchId = student?.batchInfo?._id;
        if (!batchId) return;
        fetchAssignmentsByBatchId(batchId)
            .then(setAssignments)
            .catch(() => setAssignments([]));
    }, [student]);

    useEffect(() => {
        if (!id) return;
        setLogsLoading(true);
        fetchLogsByStudentId(id)
            .then((res) => setLogs(res?.data || []))
            .catch(() => setLogs([]))
            .finally(() => setLogsLoading(false));
    }, [id]);

    if (loading) {
        return (
            <div className="overlay">
                <div className="custom-loader"></div>
                <p className="loading-text">Loading...</p>
            </div>
        );
    }

    if (error || !student) {
        return <p className="error-text">{error || 'No student data found'}</p>;
    }


    // batchInfos carries every batch the student is enrolled in (primary first).
    // Falls back to the separately-fetched primary batch for older payloads.
    const enrolledBatches = Array.isArray(student?.batchInfos) && student.batchInfos.length
        ? student.batchInfos
        : (batch ? [batch] : []);
    const performance = Array.isArray(student.performance) ? student.performance : [];

    const handleEdit = () => {
        navigate(`/students/${student._id}/update`);
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    };

    const InfoRow = ({ label, value }) => (
        <div className="sd-info-row">
            <span className="sd-info-label">{label}</span>
            <span className="sd-info-value">{value || "—"}</span>
        </div>
    );

    return (
        <div className="student-details-container">
            {/* Page Header */}
            <div className="student-header">
                <div className="student-header-title">
                    <div className="student-avatar">{student.studentName?.charAt(0)?.toUpperCase()}</div>
                    <div>
                        <h1>{student.studentName}</h1>
                        <span className={`sd-status-badge sd-status-${student.status?.toLowerCase().replace(/\s+/g, '-')}`}>
                            {student.status || "Unknown"}
                        </span>
                        {/* Activity tracking (Edit student), shown like a trainer's. */}
                        <span title={student.trackingEnabled !== false ? "The student's app activity is recorded" : "Nothing the student does in the app is recorded"} style={{
                            marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                            background: student.trackingEnabled !== false ? "#ede9fe" : "#f1f5f9",
                            color: student.trackingEnabled !== false ? "#6d28d9" : "#64748b",
                        }}>
                            {student.trackingEnabled !== false ? "Tracking ON" : "Tracking OFF"}
                        </span>
                    </div>
                </div>
                <div className="student-header-actions">
                    <button className="notif-trigger-btn" onClick={() => setShowNotificationModal(true)}>
                        Send Notification
                    </button>
                    <button className="edit-btn" onClick={handleEdit}>Edit</button>
                </div>
            </div>

            <SendNotificationModal
                isOpen={showNotificationModal}
                onClose={() => setShowNotificationModal(false)}
                student={student}
                batch={batch}
                assignments={assignments}
            />

            <div className="student-details-layout">
                {/* Right Sidebar */}
                <div className="batch-right-section">
                    {/* Batch Card — a student can be enrolled in several batches;
                        the first is their primary. */}
                    <div className="batch-card">
                        <h3 className="sd-card-heading">
                            {enrolledBatches.length > 1 ? `Enrolled Batches (${enrolledBatches.length})` : "Enrolled Batch"}
                        </h3>
                        {enrolledBatches.length ? (
                            enrolledBatches.map((b, i) => (
                                <div className="sd-batch-info" key={b._id || i}>
                                    <div className="sd-batch-title">
                                        {b.batchTitle}
                                        {enrolledBatches.length > 1 && i === 0 && (
                                            <span className="sd-primary-tag">Primary</span>
                                        )}
                                    </div>
                                    <InfoRow label="Duration" value={b.batchDuration} />
                                    <InfoRow label="Time" value={b.batchTime} />
                                    <InfoRow label="Days" value={Array.isArray(b.batchDays) ? b.batchDays.join(', ') : 'N/A'} />
                                    <InfoRow label="Status" value={
                                        <span className={`sd-status-badge sd-status-${b.batchStatus?.toLowerCase()}`}>
                                            {b.batchStatus}
                                        </span>
                                    } />
                                    {b.batchDescription && (
                                        <p className="sd-batch-desc">{b.batchDescription}</p>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p className="sd-empty-text">No batch assigned</p>
                        )}
                    </div>

                    {/* Fee Summary Card */}
                    <div className="batch-card sd-fee-card">
                        <h3 className="sd-card-heading">Fee Summary</h3>
                        <div className="sd-fee-row">
                            <span>Total Fee</span>
                            <strong>₹{student.totalFee || "—"}</strong>
                        </div>
                        <div className="sd-fee-row">
                            <span>Fee Paid</span>
                            <strong className="sd-fee-paid">₹{student.feePaid || "—"}</strong>
                        </div>
                        {student.totalFee && student.feePaid && (
                            <div className="sd-fee-row">
                                <span>Balance</span>
                                <strong className="sd-fee-balance">
                                    ₹{Number(student.totalFee) - Number(student.feePaid)}
                                </strong>
                            </div>
                        )}
                    </div>

                    {/* Meta Card */}
                    <div className="batch-card">
                        <h3 className="sd-card-heading">Record Info</h3>
                        <InfoRow label="Created" value={formatDateTime(student.createdDateTime)} />
                        <InfoRow label="Updated" value={formatDateTime(student.updatedDateTime)} />
                    </div>
                </div>

                {/* Main Content */}
                <div className="student-left-section">
                    {/* Contact & Personal */}
                    <div className="student-card-block">
                        <h3 className="sd-section-heading">Contact Information</h3>
                        <div className="sd-grid">
                            <InfoRow label="Email" value={
                                Array.isArray(student.emailId)
                                    ? student.emailId.join(', ')
                                    : student.emailId
                            } />
                            <InfoRow label="Contact" value={student.contactNumber} />
                            <InfoRow label="Alternate Contact" value={student.alternateContactNumber} />
                            <InfoRow label="Date of Birth" value={
                                student.DOB
                                    ? new Date(student.DOB).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                    : null
                            } />
                            <InfoRow label="Heard From" value={student.heardFrom} />
                        </div>
                    </div>

                    {/* Address */}
                    <div className="student-card-block">
                        <h3 className="sd-section-heading">Address</h3>
                        <div className="sd-grid">
                            <InfoRow label="Permanent Address" value={student.permanentAddress} />
                            <InfoRow label="City" value={student.permanentAddressCity} />
                            <InfoRow label="State" value={student.permanentAddressState} />
                            <InfoRow label="Alternate Address" value={student.alternateAddress} />
                            <InfoRow label="Alt. City" value={student.alternateAddressCity} />
                            <InfoRow label="Alt. State" value={student.alternateAddressState} />
                        </div>
                    </div>

                    {/* Family */}
                    <div className="student-card-block">
                        <h3 className="sd-section-heading">Family Details</h3>
                        <div className="sd-grid">
                            <InfoRow label="Father Name" value={student.fatherName} />
                            <InfoRow label="Father Occupation" value={student.fatherOccupation} />
                            <InfoRow label="Father Contact" value={student.fatherContactNumber} />
                            <InfoRow label="Mother Name" value={student.motherName} />
                            <InfoRow label="Mother Occupation" value={student.motherOccupation} />
                            <InfoRow label="Mother Contact" value={student.motherContactNumber} />
                        </div>
                    </div>

                    {/* Education */}
                    <div className="student-card-block">
                        <h3 className="sd-section-heading">Education</h3>
                        <div className="sd-grid">
                            {student.educationLevel ? (
                                <>
                                    <InfoRow label="Level" value={student.educationLevel} />
                                    <InfoRow
                                        label={student.educationLevel === "Schooling" ? "School Name" : "College / Institution"}
                                        value={student.schoolCollege}
                                    />
                                    <InfoRow label="City" value={student.schoolCollegePlace} />
                                    <InfoRow
                                        label={student.educationLevel === "Schooling" ? "Class / Grade" : "Branch / Specialization"}
                                        value={student.branch}
                                    />
                                    <InfoRow label="Score / CGPA" value={student.score} />
                                    <InfoRow
                                        label="Status"
                                        value={
                                            student.currentlyPursuing
                                                ? `Currently Pursuing${student.completionYear ? ` (expected ${student.completionYear})` : ""}`
                                                : `Completed in ${student.completedIn || "—"}`
                                        }
                                    />
                                </>
                            ) : (
                                <span className="sd-empty-text">No education details recorded</span>
                            )}
                        </div>
                    </div>

                    {/* Course */}
                    <div className="student-card-block">
                        <h3 className="sd-section-heading">Course & Skills</h3>
                        <div className="sd-grid">
                            <InfoRow label="Enrolling For" value={student.enrollingForCourse} />
                            <InfoRow label="Enrolled For" value={student.enrolledForCourse} />
                            <InfoRow label="Skills Known" value={student.skillsKnownAlready} />
                        </div>
                    </div>

                    {/* Performance */}
                    <div className="student-card-block">
                        <div className="student-performance-header">
                            <h3 className="sd-section-heading" style={{ margin: 0 }}>Performance</h3>
                            <button className="add-btn" onClick={() => setShowModal(true)}>
                                + Add
                            </button>
                        </div>
                        <AddPerformanceModal
                            isOpen={showModal}
                            onClose={() => setShowModal(false)}
                            studentId={student._id}
                            onPerformanceAdded={handlePerformanceAdded}
                        />
                        <table className="student-details-table" style={{ marginTop: "12px" }}>
                            <thead>
                                <tr>
                                    <th>Note</th>
                                    <th style={{ width: "100px" }}>Rating</th>
                                </tr>
                            </thead>
                            <tbody>
                                {performance.length > 0 ? performance.map((item) => (
                                    <tr key={item._id}>
                                        <td>{item.note}</td>
                                        <td>
                                            <span className="sd-rating">{item.rating}</span>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="2" className="sd-empty-text" style={{ textAlign: "center", padding: "20px" }}>
                                            No performance records yet
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Activity Logs */}
                    <div className="student-card-block">
                        <h3 className="sd-section-heading">Activity Logs</h3>
                        {logsLoading ? (
                            <p className="sd-empty-text" style={{ textAlign: "center", padding: "20px" }}>Loading logs…</p>
                        ) : (
                            <table className="student-details-table" style={{ marginTop: "12px" }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: "160px" }}>Date &amp; Time</th>
                                        <th style={{ width: "140px" }}>Action</th>
                                        <th style={{ width: "120px" }}>Page</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.length > 0 ? logs.map((log) => (
                                        <tr key={log._id}>
                                            <td style={{ whiteSpace: "nowrap" }}>
                                                {new Date(log.timestamp).toLocaleString("en-IN", {
                                                    day: "2-digit", month: "short", year: "numeric",
                                                    hour: "2-digit", minute: "2-digit",
                                                })}
                                            </td>
                                            <td>
                                                <span className={`sd-log-badge sd-log-${log.action}`}>
                                                    {log.action.replace(/_/g, " ")}
                                                </span>
                                            </td>
                                            <td>{log.page || "—"}</td>
                                            <td>{log.details || "—"}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="4" className="sd-empty-text" style={{ textAlign: "center", padding: "20px" }}>
                                                No activity logs yet
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <NotificationHistorySection entityType="student" entityId={student._id} />
                </div>
            </div>
        </div>
    );
};

export default StudentDetails;
